import React, { useState, useEffect, useRef, useCallback } from 'react';
import FormInput from '../FormInput';
import {
  UserPlusIcon,
  DocumentArrowUpIcon,
  InformationCircleIcon,
  ArrowLeftIcon,
  UsersIcon,
  EnvelopeIcon,
  IdentificationIcon,
  CheckCircleIcon,
  HomeIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  UserCircleIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { ArrowRightIcon, CheckIcon } from '@heroicons/react/20/solid';

const NewMemberPage = ({
  memberForm,
  fund,
  memberFormErrors,
  invoicingMembersPath,
  uploadMembersPath,
  exampleSpreadsheetPath,
  searchUsersPath,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [autoGenerateMembershipId, setAutoGenerateMembershipId] = useState(false);
  const [formData, setFormData] = useState({
    first_name: memberForm?.first_name || '',
    last_name: memberForm?.last_name || '',
    email: memberForm?.email || '',
    membership_id: memberForm?.membership_id || '',
    dues_amount: memberForm?.dues_amount || '',
  });
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);

  // Debounced search
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const response = await fetch(`${searchUsersPath}?q=${encodeURIComponent(searchQuery)}`);
        const data = await response.json();
        setSearchResults(data.users || []);
        setShowDropdown(true);
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, searchUsersPath]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        searchRef.current &&
        !searchRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setFormData({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      membership_id: '',
      dues_amount: formData.dues_amount,
    });
    setSearchQuery('');
    setShowDropdown(false);
  };

  const handleClearSelection = () => {
    setSelectedUser(null);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      membership_id: '',
      dues_amount: '',
    });
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-6 py-6">
          {/* Breadcrumb */}
          <nav className="hidden sm:flex mb-4" aria-label="Breadcrumb">
            <ol role="list" className="flex items-center space-x-2">
              <li>
                <a href="/dashboard" className="text-gray-400 hover:text-gray-500 transition-colors">
                  <HomeIcon className="w-4 h-4" />
                </a>
              </li>
              <li>
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                  <a href="/dashboard/invoicing" className="ml-2 text-sm text-gray-500 hover:text-gray-700 transition-colors">
                    Invoicing
                  </a>
                </div>
              </li>
              <li>
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                  <a href={invoicingMembersPath} className="ml-2 text-sm text-gray-500 hover:text-gray-700 transition-colors">
                    {fund.name}
                  </a>
                </div>
              </li>
              <li>
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="ml-2 text-sm font-medium text-gray-700">Add Member</span>
                </div>
              </li>
            </ol>
          </nav>

          {/* Header with title */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center flex-shrink-0">
                <UserPlusIcon className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Add Member</h1>
                <p className="mt-1 text-sm text-gray-500">
                  Add a member to <span className="font-medium text-gray-700">{fund.name}</span>
                </p>
              </div>
            </div>

            {/* Quick action: Upload instead */}
            <a
              href={uploadMembersPath}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors"
            >
              <DocumentArrowUpIcon className="w-4 h-4" />
              Bulk Upload
              <ArrowRightIcon className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-6">
        <div className="max-w-3xl mx-auto space-y-6">

          {/* Search Existing Users Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-transparent rounded-t-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <MagnifyingGlassIcon className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-900">Find Existing User</h2>
                  <p className="text-sm text-gray-500">Search for someone already in your organization</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              {selectedUser ? (
                /* Selected User Card */
                <div className="relative bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-200 rounded-xl p-5">
                  <button
                    type="button"
                    onClick={handleClearSelection}
                    className="absolute top-3 right-3 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                  <div className="flex items-start gap-4">
                    <div className="h-14 w-14 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center flex-shrink-0">
                      <span className="text-lg font-bold text-white">{selectedUser.initials}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-gray-900">{selectedUser.full_name}</h3>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                          <CheckIcon className="w-3 h-3" />
                          Selected
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{selectedUser.email}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        {selectedUser.phone && (
                          <span>{selectedUser.phone}</span>
                        )}
                        <span>In {selectedUser.funds_count} fund{selectedUser.funds_count !== 1 ? 's' : ''}</span>
                        {selectedUser.joined_at && (
                          <span>Joined {selectedUser.joined_at}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-emerald-200">
                    <p className="text-sm text-emerald-700 flex items-center gap-2">
                      <SparklesIcon className="w-4 h-4" />
                      This user will be added to <strong>{fund.name}</strong>. Just add their membership details below.
                    </p>
                  </div>
                </div>
              ) : (
                /* Search Input */
                <div className="relative" ref={searchRef}>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
                      {isSearching ? (
                        <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onFocus={() => searchQuery.length >= 2 && setShowDropdown(true)}
                      className="block w-full rounded-xl border-gray-300 pl-12 pr-4 py-3 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500 placeholder:text-gray-400"
                      placeholder="Search by name or email..."
                    />
                  </div>

                  {/* Search Results Dropdown */}
                  {showDropdown && (
                    <div
                      ref={dropdownRef}
                      className="absolute z-10 mt-2 w-full bg-white rounded-xl shadow-lg border border-gray-200 max-h-80 overflow-auto"
                    >
                      {searchResults.length > 0 ? (
                        <ul className="py-2">
                          {searchResults.map((user) => (
                            <li key={user.id}>
                              <button
                                type="button"
                                onClick={() => handleSelectUser(user)}
                                className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
                              >
                                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center flex-shrink-0">
                                  <span className="text-sm font-medium text-gray-600">{user.initials}</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">{user.full_name}</p>
                                  <p className="text-xs text-gray-500 truncate">{user.email}</p>
                                </div>
                                <div className="text-xs text-gray-400">
                                  {user.funds_count} fund{user.funds_count !== 1 ? 's' : ''}
                                </div>
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="px-4 py-6 text-center">
                          <UserCircleIcon className="mx-auto h-10 w-10 text-gray-300" />
                          <p className="mt-2 text-sm text-gray-500">No users found matching "{searchQuery}"</p>
                          <p className="text-xs text-gray-400 mt-1">You can create a new user below</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {!selectedUser && (
                <p className="mt-3 text-xs text-gray-500 text-center">
                  Or fill out the form below to create a new user
                </p>
              )}
            </div>
          </div>

          {/* Member Form Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            {/* Card Header */}
            <div className={`px-6 py-4 border-b border-gray-100 rounded-t-xl ${selectedUser ? 'bg-gradient-to-r from-emerald-50 to-transparent' : 'bg-gradient-to-r from-gray-50 to-transparent'}`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${selectedUser ? 'bg-emerald-100' : 'bg-gray-100'}`}>
                  {selectedUser ? (
                    <CheckCircleIcon className="w-5 h-5 text-emerald-600" />
                  ) : (
                    <UsersIcon className="w-5 h-5 text-gray-600" />
                  )}
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-900">
                    {selectedUser ? 'Membership Details' : 'New Member Information'}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {selectedUser
                      ? `Complete the membership details for ${selectedUser.first_name}`
                      : 'Fill in the details to create a new member'}
                  </p>
                </div>
              </div>
            </div>

            <form action={invoicingMembersPath} method="post" encType="multipart/form-data">
              <input
                type="hidden"
                name="authenticity_token"
                value={document.getElementsByName('csrf-token')[0]?.content}
              />
              {selectedUser && (
                <input type="hidden" name="user[existing_user_id]" value={selectedUser.id} />
              )}

              <div className="p-6 space-y-6">
                {/* Personal Information Section - Only show if no user selected */}
                {!selectedUser && (
                  <>
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <UsersIcon className="w-5 h-5 text-gray-400" />
                        <h3 className="text-sm font-medium text-gray-700">Personal Details</h3>
                      </div>
                      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                        <div>
                          <label htmlFor="user_first_name" className="block text-sm font-medium text-gray-700 mb-1">
                            First Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="user[first_name]"
                            id="user_first_name"
                            value={formData.first_name}
                            onChange={(e) => handleInputChange('first_name', e.target.value)}
                            required
                            placeholder="John"
                            className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                          />
                          {memberFormErrors?.messages?.first_name && (
                            <p className="mt-1 text-sm text-red-600">{memberFormErrors.messages.first_name.join(', ')}</p>
                          )}
                        </div>
                        <div>
                          <label htmlFor="user_last_name" className="block text-sm font-medium text-gray-700 mb-1">
                            Last Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="user[last_name]"
                            id="user_last_name"
                            value={formData.last_name}
                            onChange={(e) => handleInputChange('last_name', e.target.value)}
                            required
                            placeholder="Smith"
                            className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                          />
                          {memberFormErrors?.messages?.last_name && (
                            <p className="mt-1 text-sm text-red-600">{memberFormErrors.messages.last_name.join(', ')}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-gray-100" />

                    {/* Contact Information Section */}
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <EnvelopeIcon className="w-5 h-5 text-gray-400" />
                        <h3 className="text-sm font-medium text-gray-700">Contact Information</h3>
                      </div>
                      <div>
                        <label htmlFor="user_email" className="block text-sm font-medium text-gray-700 mb-1">
                          Email Address <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="user[email]"
                          id="user_email"
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          required
                          placeholder="john.smith@example.com"
                          className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                        />
                        <p className="mt-1.5 text-xs text-gray-500">The member will receive communications at this email address.</p>
                        {memberFormErrors?.messages?.email && (
                          <p className="mt-1 text-sm text-red-600">{memberFormErrors.messages.email.join(', ')}</p>
                        )}
                      </div>
                    </div>

                    <div className="border-t border-gray-100" />
                  </>
                )}

                {/* Membership Details Section - Always show */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <IdentificationIcon className="w-5 h-5 text-gray-400" />
                    <h3 className="text-sm font-medium text-gray-700">Membership Details</h3>
                  </div>
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="user_membership_id" className="block text-sm font-medium text-gray-700 mb-1">
                        Membership ID {!autoGenerateMembershipId && <span className="text-red-500">*</span>}
                      </label>
                      <input
                        type="text"
                        name="user[membership_id]"
                        id="user_membership_id"
                        value={autoGenerateMembershipId ? '' : formData.membership_id}
                        onChange={(e) => handleInputChange('membership_id', e.target.value)}
                        required={!autoGenerateMembershipId}
                        disabled={autoGenerateMembershipId}
                        placeholder="EMP-001"
                        className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm disabled:bg-gray-100 disabled:text-gray-400"
                      />
                      {/* Always submit the auto-generate choice; disabled inputs are not submitted. */}
                      <input
                        type="hidden"
                        name="user[auto_generate_membership_id]"
                        value={autoGenerateMembershipId ? 'true' : 'false'}
                      />
                      <label className="mt-2 flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={autoGenerateMembershipId}
                          onChange={(e) => setAutoGenerateMembershipId(e.target.checked)}
                          className="form-checkbox h-4 w-4 rounded text-indigo-600 focus:ring-indigo-500"
                        />
                        Auto-generate Membership ID
                      </label>
                      <p className="mt-1.5 text-xs text-gray-500">
                        {autoGenerateMembershipId
                          ? 'A unique Membership ID will be generated automatically.'
                          : 'A unique identifier (e.g., employee ID, union card number)'}
                      </p>
                      {memberFormErrors?.messages?.membership_id && (
                        <p className="mt-1 text-sm text-red-600">{memberFormErrors.messages.membership_id.join(', ')}</p>
                      )}
                    </div>

                    {/* Dues Amount - only show for individual dues calculation */}
                    {fund.dues_calculation_type === 'individual' && (
                      <div>
                        <label htmlFor="user_dues_amount" className="block text-sm font-medium text-gray-700 mb-1">
                          Dues Amount <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          name="user[dues_amount]"
                          id="user_dues_amount"
                          value={formData.dues_amount}
                          onChange={(e) => handleInputChange('dues_amount', e.target.value)}
                          required
                          placeholder="0.00"
                          step="0.01"
                          min="0"
                          className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                        />
                        <p className="mt-1.5 text-xs text-gray-500">The member's recurring dues amount</p>
                        {memberFormErrors?.messages?.dues_amount && (
                          <p className="mt-1 text-sm text-red-600">{memberFormErrors.messages.dues_amount.join(', ')}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Info Box */}
                <div className={`p-4 border rounded-xl ${selectedUser ? 'bg-emerald-50 border-emerald-100' : 'bg-blue-50 border-blue-100'}`}>
                  <div className="flex gap-3">
                    <div className="flex-shrink-0">
                      <InformationCircleIcon className={`h-5 w-5 ${selectedUser ? 'text-emerald-500' : 'text-blue-500'}`} />
                    </div>
                    <div>
                      <h4 className={`text-sm font-medium ${selectedUser ? 'text-emerald-900' : 'text-blue-900'}`}>
                        {selectedUser ? 'Adding existing user to fund' : 'What happens next?'}
                      </h4>
                      <ul className={`mt-2 text-sm space-y-1.5 ${selectedUser ? 'text-emerald-700' : 'text-blue-700'}`}>
                        {selectedUser ? (
                          <>
                            <li className="flex items-start gap-2">
                              <CheckCircleIcon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${selectedUser ? 'text-emerald-500' : 'text-blue-500'}`} />
                              <span><strong>{selectedUser.full_name}</strong> will be added to <strong>{fund.name}</strong></span>
                            </li>
                            <li className="flex items-start gap-2">
                              <CheckCircleIcon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${selectedUser ? 'text-emerald-500' : 'text-blue-500'}`} />
                              <span>Their existing account will be linked to this fund membership</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <CheckCircleIcon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${selectedUser ? 'text-emerald-500' : 'text-blue-500'}`} />
                              <span>You can create invoices for them after adding</span>
                            </li>
                          </>
                        ) : (
                          <>
                            <li className="flex items-start gap-2">
                              <CheckCircleIcon className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                              <span>The member will be created and added to <strong>{fund.name}</strong></span>
                            </li>
                            <li className="flex items-start gap-2">
                              <CheckCircleIcon className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                              <span>If the email already exists, this membership will be linked to that account</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <CheckCircleIcon className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                              <span>New members will receive a welcome email with login instructions</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <CheckCircleIcon className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                              <span>You can create invoices for this member after they're added</span>
                            </li>
                          </>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Form Footer */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 rounded-b-xl">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <a
                    href={invoicingMembersPath}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    <ArrowLeftIcon className="w-4 h-4" />
                    Back to Members
                  </a>
                  <div className="flex items-center gap-3">
                    <a
                      href={invoicingMembersPath}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </a>
                    <button
                      type="submit"
                      className={`inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white rounded-lg shadow-sm transition-colors ${
                        selectedUser
                          ? 'bg-emerald-600 hover:bg-emerald-700'
                          : 'bg-indigo-600 hover:bg-indigo-700'
                      }`}
                    >
                      <UserPlusIcon className="w-4 h-4" />
                      {selectedUser ? `Add ${selectedUser.first_name} to Fund` : 'Create & Add Member'}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>

          {/* Bulk Upload Banner */}
          <div className="bg-gradient-to-r from-indigo-50 via-blue-50 to-indigo-50 border border-indigo-100 rounded-xl p-5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                <DocumentArrowUpIcon className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-indigo-900">Need to add multiple members?</h3>
                <p className="mt-1 text-sm text-indigo-700">
                  Upload a spreadsheet to import many members at once, create invoices, and send notifications automatically.
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-4">
                  <a
                    href={uploadMembersPath}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-500"
                  >
                    Go to Upload Page
                    <ArrowRightIcon className="w-4 h-4" />
                  </a>
                  <a
                    href={`${exampleSpreadsheetPath}.xlsx`}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Download template
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewMemberPage;
